import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid, Divider } from '@material-ui/core';
import CustomTable from 'components/CustomTable';
import NumberListFormItem from 'components/Application/InitialApplication/NumberListFormItem';
import CustomTextField from 'components/Input/CustomTextField';
import CustomButton from 'components/CustomInput/Button';
import useDeclarationAndEndorsementData from 'hooks/tableData/useDeclarationAndEndorsementData';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import { useSelector, useDispatch } from 'react-redux';
import {
    UPDATE_APPLICATION_DETAIL_POST_DATA,
    ADD_DELETE_REQUEST,
    UPDATE_FORM_VALIDITY,
    SET_DECLARATION_AND_ENDORSEMENT,
    RESET_DECLARATION_AND_ENDORSEMENT,
} from 'store/key';
import TextHeader from 'components/CustomText/TextHeader';
import { checkDeclarationAndEndorsementCompleted } from 'utility/formValidate';
import {
    declareStudentKey,
    declareInvKey,
    HAEndorsementKey,
    universitiesEndorsementKey,
    HAUniversitiesEndorsementKey,
} from 'constants/globalEnum';
import DeclarationNormalTable from './DeclarationNormalTable';
import DeclarationStudentTable from './DeclarationStudentTable';
import { isArray } from 'lodash';
import DataListener from './DataListener';

const useStyles = makeStyles((theme) => ({
    paperMargin: {
        margin: 10,
    },
    gridPadding: {
        padding: 10,
    },
    formTitleWeight: {
        fontWeight: 'bold',
    },
    title: {
        paddingBottom: theme.spacing(1),
        fontWeight: 'bold',
    },
    item: {
        paddingBottom: theme.spacing(1),
    },
    textContent: {
        paddingLeft: theme.spacing(4),
    },
    textItem: {
        paddingBottom: theme.spacing(1),
    },
    textItemSign: {
        paddingRight: theme.spacing(2),
    },
    name: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        color: '#f44336',
    },
    studyTitle: {
        fontSize: 14,
        marginBottom: theme.spacing(1),
    },
    sectionItem: {
        marginBottom: theme.spacing(2),
    },
}));

export default function DeclarationAndEndorsementFormLayout(props) {
    const classes = useStyles();
    const { siteInfoId, formId, disableOnPermission, isAmendmentEdit, isAmendmentCreate, applicationCreateDtm } = props;
    const dispatch = useDispatch();

    const isAmendmentForm = isAmendmentEdit || isAmendmentCreate;

    const applicationDetail = useSelector((state) => state.applicationDetail[formId]);
    const applicationDetailPostData = useSelector((state) => state.applicationDetailPostData);
    const applicationDetailValid = useSelector((state) => state.applicationDetailValid);
    const unSaveDeletionIds = useSelector((state) => state.declarationAndEndorsement.unSaveDeletionIds);
    const removeRequestCoiIds = useSelector((state) => state.declarationAndEndorsement.removeRequestCoiIds);

    const deleteRequest = applicationDetailPostData?.deleteRequest;

    const siteInfoInsideValid = applicationDetailValid.siteInfoInsideValid[siteInfoId];
    const declareInvValid = siteInfoInsideValid?.declareInvValid;
    const declareStudentValid = siteInfoInsideValid?.declareStudentValid;

    const p1ScientificTitle = applicationDetail.applicationForm?.p1ScientificTitle ?? '';
    const postData = applicationDetailPostData.postData ?? {};

    const siteIndex = applicationDetail.siteInfo.findIndex((i) => i.id === siteInfoId);

    const siteInfo = applicationDetail.siteInfo[siteIndex] ?? {};
    const postSiteList = postData.siteInfo ?? [];

    const postSiteIndex = postSiteList.findIndex((i) => i.id === siteInfoId);
    const postSiteInfo = postSiteList[postSiteIndex] ?? {};

    const postDeclareStudent = postData.declareStudent ?? [];
    const postDeclareInv = postData.declareInv ?? [];
    const postDeclare = postData.declare ?? [];

    const {
        declareInvColumns,
        declareInvRows,
        HAEndorsementColumns,
        HAEndorsementRows,
        universitiesEndorsementColumns,
        universitiesEndorsementRows,
        HAUniversitiesEndorsementColumns,
        HAUniversitiesEndorsementRows,
        formData,
        declareStudentColumns,
        declareStudentRows,
        loadData,
    } = useDeclarationAndEndorsementData({
        ...props,
        applicationDetail,
        siteInfoId,
        removeRequestCoiIds,
        formId,
    });

    const [selectedRow, setSelectedRow] = useState(null);
    const [openPopup, setOpenPopup] = useState(false);
    const [popupType, setPopupType] = useState(null);

    /** Personal Particular 2 = Student(site info) */
    const postStudent = postSiteInfo.student;
    const [student, setStudent] = useState(siteInfo.student);

    /** Co-investigators */
    const postCoiInvolved = postSiteInfo.coiInvolved;
    const [coiInvolved, setCoiInvolved] = useState(siteInfo.coiInvolved);

    const deleteCoi = applicationDetailPostData.deleteRequest?.coi ?? [];

    /** Auto entry for the same site PI*/
    // useEffect(() => {
    // const site = applicationDetail.siteInfo.find((item) => {
    //     return item.id === siteInfoId;
    // });
    // const surname = site?.['surname'];
    // const firstName = site?.['firstName'];
    // const title = site?.['title'];
    // if (surname && firstName && title) {
    //     dispatch({
    //         type: 'UPDATE_DECLARATION_NORMAL_POST_DATA',
    //         payload: {
    //             formId,
    //             siteInfoId,
    //             firstName: firstName,
    //             surname: surname,
    //             title: title,
    //             roleNameIndex: 0,
    //         },
    //     });
    // }
    // }, [applicationDetail]);

    /** reset value when applicationDetailPostData change */
    useEffect(() => {
        /** P1*/
        setStudent(postStudent !== undefined ? postStudent : siteInfo.student);
        /** Co */
        setCoiInvolved(postCoiInvolved !== undefined ? postCoiInvolved : siteInfo.coiInvolved);

        // eslint-disable-next-line
    }, [postStudent, postCoiInvolved]);

    const deleteRow = (row, type) => {
        if (!row || !type) return;
        const list = formData[type].rows;
        const rowsCopy = [...list];
        const index = list.findIndex((item, index) => {
            return item?.id === row?.id;
        });
        if (index !== -1) {
            deleteTableRow({ row, type, isHandleDelete: true });
            rowsCopy.splice(index, 1);
            formData[type].setRows(rowsCopy);
        }
    };

    const deleteTableRow = ({ row, type, isHandleDelete }) => {
        if (row.existInDB) {
            let key = 'declare';
            if (type === declareInvKey) {
                key = 'declareInv';
            } else if (type === declareStudentKey) {
                key = declareStudentKey;
            }
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: key,
                    id: row.id,
                    siteInfoId: siteInfoId,
                    applicationFormId: formId,
                },
            });
            if (coiInvolved && type === declareInvKey) {
                dispatch({
                    type: SET_DECLARATION_AND_ENDORSEMENT,
                    payload: {
                        removeRequestCoiIds: [...removeRequestCoiIds, row?.id],
                    },
                });
            }

            if (isHandleDelete) {
                dispatch({
                    type: SET_DECLARATION_AND_ENDORSEMENT,
                    payload: {
                        unSaveDeletionIds: [...unSaveDeletionIds, row?.id],
                    },
                });
            }
        } else {
            deletePostData(row, type);
        }
    };

    /** delete postData */
    const deletePostData = (row, type) => {
        let key = 'declare';
        let postData = postDeclare;
        if (type === declareInvKey) {
            key = 'declareInv';
            postData = postDeclareInv;
        } else if (type === declareStudentKey) {
            key = declareStudentKey;
            postData = postDeclareStudent;
        }

        /** delete record when repeated */
        const existedIndex = postData.findIndex((i) => i?.id === row?.id);
        if (existedIndex !== -1) {
            postData.splice(existedIndex, 1);

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: key,
                    data: postData,
                    applicationFormId: formId,
                },
            });
        }
    };

    const handleEditClick = (row, type) => {
        setSelectedRow(row);
        setPopupType(type);
        setOpenPopup(true);
    };
    const editRow = (row) => {
        const list = formData[popupType].rows;
        const rowsCopy = [...list];
        const index = list.findIndex((item, index) => item.id === row.id);
        if (index !== -1) {
            rowsCopy[index] = { ...row };
            formData[popupType].setRows(rowsCopy);
            updatePostData(row);
        }
    };
    const handleAddClick = (type) => {
        setSelectedRow(null);
        setPopupType(type);
        setOpenPopup(true);
    };

    const updatePostData = (row, type) => {
        const newPopupType = type ?? popupType;
        let key = 'declare';
        let postData = postDeclare;
        let requestData = {
            ...row,
            siteInfo: { id: siteInfoId },
        };
        if (row.signDate) {
            requestData.signDate = new Date(row.signDate);
        }

        if (newPopupType === declareInvKey) {
            key = 'declareInv';
            postData = postDeclareInv;
        } else if (newPopupType === declareStudentKey) {
            key = declareStudentKey;
            postData = postDeclareStudent;
        } else {
            requestData.endorse = newPopupType;
        }
        /** delete record when repeated */
        const existedIndex = postData.findIndex((i) => i?.id === requestData?.id);
        if (existedIndex !== -1) {
            postData.splice(existedIndex, 1);
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: key,
                data: [...postData, requestData],
                applicationFormId: formId,
            },
        });
    };

    const updateDeclareStudentPostDatas = (rows) => {
        /** update DeclareStudent PostDatas when check/uncheck student(checkbox) */
        let key = declareStudentKey;
        let postData = postDeclareStudent;
        const newRows = rows.filter((item) => !item.existInDB);
        let requestData = newRows.map((item) => {
            let row = {
                ...item,
                siteInfo: { id: siteInfoId },
            };
            if (item.signDate) {
                item.signDate = new Date(row.signDate);
            }

            /** delete record when repeated */
            const existedIndex = postData.findIndex((i) => i?.id === item?.id);
            if (existedIndex !== -1) {
                postData.splice(existedIndex, 1);
            }

            return row;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: key,
                data: [...postData, ...requestData],
                applicationFormId: formId,
            },
        });
    };

    const onSubmit = (data, type) => {
        let row = { ...data };
        const key = type ?? popupType;
        const list = formData[key].rows;
        let datas = [];
        if (isArray(data)) {
            /** only type === declareStudentKey will trigger this logic */
            datas = data;
            updateDeclareStudentPostDatas(datas);
        } else {
            datas = [...list, row];
            updatePostData(data);
        }
        formData[key].setRows(datas);
    };

    /** check Declaration And Endorsement for completeness */
    useEffect(() => {
        let tableAColumnCompleted = declareInvValid && declareStudentValid;
        const tablesValid = checkDeclarationAndEndorsementCompleted({
            tableAColumnCompleted: tableAColumnCompleted,
            HAEndorsementRows,
            universitiesEndorsementRows,
            siteInfoId,
        });

        dispatch({
            type: UPDATE_FORM_VALIDITY,
            payload: {
                type: 'site',
                applicationFormId: formId,
                siteInfoId: siteInfoId,
                formKey: 'declarationAndEndorsementValid',
                isValid: tableAColumnCompleted && tablesValid,
            },
        });
        // eslint-disable-next-line
    }, [declareStudentValid, declareInvValid, HAEndorsementRows, universitiesEndorsementRows]);
    /** end */

    const isHighlight = (type) => {
        let ishl = false;
        if (type === 'declareInv') {
            const changedItem = applicationDetail?.changedItem?.declareInv;
            if (
                changedItem &&
                changedItem.declareInv &&
                changedItem.declareInv[`${siteInfoId}`] &&
                Object.keys(changedItem.declareInv[`${siteInfoId}`]).length
            ) {
                ishl = true;
            }
        } else if (type === 'declareStudent') {
            const changedItem = applicationDetail?.changedItem?.declareInv;
            if (
                changedItem &&
                changedItem.declareInv &&
                changedItem.declareInv[`${siteInfoId}`] &&
                changedItem.declareInv[`${siteInfoId}`].STUDENT &&
                changedItem.declareInv[`${siteInfoId}`].STUDENT.length
            ) {
                ishl = true;
            }
        } else {
            const changedItem = applicationDetail?.changedItem?.declare;
            if (
                changedItem &&
                changedItem.declare &&
                changedItem.declare[`${siteInfoId}`] &&
                changedItem.declare[`${siteInfoId}`][`${type}`] &&
                changedItem.declare[`${siteInfoId}`][`${type}`].length
            ) {
                ishl = true;
            }
        }
        return ishl;
    };

    const mapShowRow = (rows, type) => {
        const key = type ?? 'declare';
        let newRows = [];
        if (deleteRequest) {
            let deleteIds = deleteRequest[key] ?? [];
            deleteIds = deleteIds.map((item) => item.id);
            newRows = rows.filter((item) => !deleteIds.includes(item.id));
        }
        return newRows;
    };

    return (
        <div>
            <DataListener
                onPostDataReset={async () => {
                    await dispatch({ type: RESET_DECLARATION_AND_ENDORSEMENT });
                    loadData();
                }}
            />
            {openPopup ? (
                <PopupInput
                    height="60%"
                    width="60%"
                    title={formData[popupType].popupTitle}
                    defaultFormList={formData[popupType].defaultFormList}
                    selectedRow={selectedRow}
                    editRow={editRow}
                    setOpenPopup={setOpenPopup}
                    tableList={formData[popupType].rows}
                    onSubmit={onSubmit}
                />
            ) : null}
            <Paper className={classes.paperMargin}>
                <div className={classes.gridPadding}>
                    <TextHeader italic underline>
                        Declaration And Endorsement
                    </TextHeader>
                </div>
                <Grid className={classes.gridPadding}>
                    <Grid
                        container
                        className={classes.item}
                        style={{
                            fontStyle: 'italic',
                        }}
                    >
                        Note: Information will be used for risk management purpose and to assist HA's finance controller
                        in sourcing insurance coverage for clinical trial activities
                    </Grid>
                    <Grid container className={classes.sectionItem}>
                        <Grid container className={classes.item}>
                            <strong>
                                <span style={{ background: isHighlight('declareInv') ? 'yellow' : 'initial' }}>
                                    A. Declaration by Principal Investigator and other Investigator(s)
                                </span>
                            </strong>
                        </Grid>
                        <Grid container className={classes.studyTitle}>
                            Scientific Title of Study:
                        </Grid>
                        <Grid container className={classes.item}>
                            <CustomTextField disabled name="text" value={p1ScientificTitle} />
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'1'} alignItems="baseline">
                                I / We declare that the information supplied is to the best of our knowledge and
                                accurate.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'2'} alignItems="baseline">
                                I / We declare that the protocol complies with Declaration of Helsinki.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'3'} alignItems="baseline">
                                I / We agree to uphold the protection of research subjects' right and safety through
                                adherence to local laws, Declaration of Helsinki, institutional policies² and whenever
                                applicable, the ICH-GCP.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'4'} alignItems="baseline">
                                I / We understand that approval by the CIRB/CREC/IRB is subject to regular renewal
                                according to local policy.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container alignItems="baseline">
                            <NumberListFormItem number={'5'} alignItems="baseline">
                                <Grid container wrap="nowrap" alignItems="center">
                                    I / We agree to report to the
                                    <Grid className={classes.name}>{`< CIRB/CREC/IRB >`}</Grid>
                                </Grid>
                            </NumberListFormItem>
                            <Grid container className={classes.textContent}>
                                <Grid container wrap="nowrap" alignItems="baseline" className={classes.textItem}>
                                    <Typography className={classes.textItemSign}>-</Typography>
                                    <Grid container>
                                        any planned change(s) to the study, and further agree not to implement any
                                        change(s) without receiving prior approval, except to eliminate immediate hazard
                                        to research subjects or when the change(s) involve only logistical or
                                        administrative issues.
                                    </Grid>
                                </Grid>
                                <Grid container wrap="nowrap" alignItems="baseline" className={classes.textItem}>
                                    <Typography className={classes.textItemSign}>-</Typography>
                                    <Grid container alignItems="center">
                                        any fatal events in applying site within the specific time according to the
                                        Standard Operating Procedures of the
                                        <Grid className={classes.name}>CIRB/CREC/IRB</Grid>
                                        while pending investigation, and any serious adverse events in applying site
                                        (with an extended report) preferably within seven days but not later than 15
                                        days (from the day it was made known to me / us).
                                    </Grid>
                                </Grid>
                                <Grid container wrap="nowrap" alignItems="baseline" className={classes.textItem}>
                                    <Typography className={classes.textItemSign}>-</Typography>
                                    <Grid container>
                                        any new information on the project that adversely influences the risk/benefit
                                        ratio.
                                    </Grid>
                                </Grid>
                                <Grid container wrap="nowrap" alignItems="baseline" className={classes.textItem}>
                                    <Typography className={classes.textItemSign}>-</Typography>
                                    <Grid container alignItems="center">
                                        progress report(s) (as requested by the
                                        <Grid className={classes.name}>CIRB/CREC/IRB</Grid>) and a final report (after
                                        completion of study).
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'6'} alignItems="baseline">
                                I / We agree to keep all study documents for a period of at least five years after
                                study closure as stated in question 10.5 of this application form.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'7'} alignItems="baseline">
                                I / We agree to maintain adequate records and to make them available for audit /
                                inspection.
                            </NumberListFormItem>
                        </Grid>
                        <Grid container>
                            <NumberListFormItem number={'8'} alignItems="baseline">
                                I / We agree to ensure that all associates, colleagues, and employees assisting in the
                                conduct of the study are informed about their obligations in meeting the above
                                commitments.
                            </NumberListFormItem>
                        </Grid>
                    </Grid>
                    {/* table A: normal table */}
                    <DeclarationNormalTable
                        siteInfoId={siteInfoId}
                        formId={formId}
                        disableOnPermission={disableOnPermission}
                        dispatch={dispatch}
                        siteInfoInsideValid={siteInfoInsideValid}
                        declareInvColumns={declareInvColumns}
                        declareInvRows={declareInvRows}
                        coiInvolved={coiInvolved}
                        deleteCoi={deleteCoi}
                        handleEditClick={handleEditClick}
                        disableOldDataDelete={isAmendmentForm}
                        applicationCreateDtm={applicationCreateDtm}
                        mapShowRow={mapShowRow}
                    />
                    {/* table A: student table */}
                    <DeclarationStudentTable
                        siteInfoId={siteInfoId}
                        formId={formId}
                        disableOnPermission={disableOnPermission}
                        deleteRow={deleteRow}
                        siteInfoInsideValid={siteInfoInsideValid}
                        declareStudentColumns={declareStudentColumns}
                        declareStudentRows={declareStudentRows}
                        dispatch={dispatch}
                        handleEditClick={handleEditClick}
                        handleAddClick={handleAddClick}
                        student={student}
                        disableOldDataDelete={isAmendmentForm}
                        applicationCreateDtm={applicationCreateDtm}
                        mapShowRow={mapShowRow}
                        isHighlight={isHighlight}
                    />

                    <Grid container style={{ marginTop: 16 }}>
                        <Grid
                            container
                            style={{
                                fontStyle: 'italic',
                            }}
                        >
                            ² HA Guide on Research Ethics (for Study Site {'&'} Research Ethics Committee) and
                            Investigator's Code of Practice; HA Clinical Data Policy Manual ; and other prevailing HA
                            policies
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '8px 0 24px' }} />

                    <Grid container className={classes.title}>
                        <span style={{ background: isHighlight('H') ? 'yellow' : 'initial' }}>
                            B. Endorsement by COS or Authorised Representative³ of HA
                        </span>
                    </Grid>
                    <Grid container className={classes.studyTitle}>
                        Scientific Title of Study:
                    </Grid>
                    <Grid container className={classes.item}>
                        <CustomTextField disabled name="title" value={p1ScientificTitle} />
                    </Grid>
                    <Grid container>
                        <NumberListFormItem number={'1'} alignItems="baseline">
                            <Grid container wrap="nowrap">
                                I endorse the application and authorise the captioned study to be undertaken in my
                                department upon approval by the
                                <Grid className={classes.name}>CIRB/CREC/IRB</Grid>.
                            </Grid>
                        </NumberListFormItem>
                    </Grid>
                    <Grid container className={classes.item}>
                        <NumberListFormItem number={'2'} alignItems="baseline">
                            I am of the opinion that the investigator(s) within my department/unit are appropriately
                            qualified within the disease / therapeutic area involved, and are capable of undertaking
                            this study in terms of their workload and time available, and that the study site(s) under
                            my supervision have access to adequate facilities and support for the research to be
                            conducted in a safe manner.
                        </NumberListFormItem>
                    </Grid>
                    <Grid container>
                        <CustomButton
                            disableOnPermission={disableOnPermission}
                            style={{ marginBottom: 8 }}
                            onClick={() => handleAddClick(HAEndorsementKey)}
                        >
                            Add
                        </CustomButton>
                        <CustomTable
                            disableOnPermission={disableOnPermission}
                            isEditAllow={true}
                            onEdit={(row) => handleEditClick(row, HAEndorsementKey)}
                            isDeleteAllow={true}
                            onDelete={(row) => deleteRow(row, HAEndorsementKey)}
                            columns={HAEndorsementColumns}
                            rows={mapShowRow?.(HAEndorsementRows)}
                            allowResize
                            fullWidthTable={false}
                            disableOldDataDelete={isAmendmentForm}
                            applicationCreateDtm={applicationCreateDtm}
                        />
                    </Grid>
                    <Grid container style={{ marginTop: 16 }}>
                        <Grid
                            container
                            style={{
                                fontStyle: 'italic',
                            }}
                        >
                            ³ Should be signed by another suitable senior staff (e.g. HCE or his/ her designate) if the
                            COS is the Applicant for the study
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '8px 0 24px' }} />

                    <Grid container className={classes.title}>
                        <span style={{ background: isHighlight('U') ? 'yellow' : 'initial' }}>
                            C. Endorsement by Head of Department⁴ of the Universities
                        </span>
                    </Grid>
                    <Grid container className={classes.studyTitle}>
                        Scientific Title of Study:
                    </Grid>
                    <Grid container className={classes.item}>
                        <CustomTextField disabled name="title" value={p1ScientificTitle} />
                    </Grid>
                    <Grid container>
                        <NumberListFormItem number={'1'} alignItems="baseline">
                            <Grid container wrap="nowrap">
                                I endorse the application and authorise the captioned study to be undertaken in my
                                department upon approval by the
                                <Grid className={classes.name}>CIRB/CREC/IRB</Grid>.
                            </Grid>
                        </NumberListFormItem>
                    </Grid>
                    <Grid container className={classes.item}>
                        <NumberListFormItem number={'2'} alignItems="baseline">
                            I am of the opinion that the investigator(s) within my department/unit are appropriately
                            qualified within the disease / therapeutic area involved, and are capable of undertaking
                            this study in terms of their workload and time available, and that the study site(s) under
                            my supervision have access to adequate facilities and support for the research to be
                            conducted in a safe manner.
                        </NumberListFormItem>
                    </Grid>
                    <Grid container>
                        <CustomButton
                            disableOnPermission={disableOnPermission}
                            style={{ marginBottom: 8 }}
                            onClick={() => handleAddClick(universitiesEndorsementKey)}
                        >
                            Add
                        </CustomButton>
                        <CustomTable
                            disableOnPermission={disableOnPermission}
                            isEditAllow={true}
                            onEdit={(row) => handleEditClick(row, universitiesEndorsementKey)}
                            isDeleteAllow={true}
                            onDelete={(row) => deleteRow(row, universitiesEndorsementKey)}
                            columns={universitiesEndorsementColumns}
                            rows={mapShowRow?.(universitiesEndorsementRows)}
                            allowResize
                            fullWidthTable={false}
                            disableOldDataDelete={isAmendmentForm}
                            applicationCreateDtm={applicationCreateDtm}
                        />
                    </Grid>
                    <Grid container style={{ marginTop: 16 }}>
                        <Grid
                            container
                            style={{
                                fontStyle: 'italic',
                            }}
                        >
                            ⁴ Should be signed by another suitable senior staff (e.g. Acting Head / Senior Member in the
                            Department) if the Head of Department is the Applicant for the study
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '8px 0 24px' }} />

                    <Grid container className={classes.title}>
                        <span style={{ background: isHighlight('C') ? 'yellow' : 'initial' }}>
                            D. Endorsement by COS(s) or Head(s) of the Collaborating Department(s) in HA or in
                            Universities
                        </span>
                    </Grid>
                    <Grid container className={classes.studyTitle}>
                        Scientific Title of Study:
                    </Grid>
                    <Grid container className={classes.item}>
                        <CustomTextField disabled name="title" value={p1ScientificTitle} />
                    </Grid>
                    <Grid container>
                        <Grid style={{ marginBottom: 8 }}>
                            I support the captioned study and verify that the workload to be incurred will not interfere
                            with the department's service priority.
                        </Grid>
                        <Grid container>
                            <CustomButton
                                disableOnPermission={disableOnPermission}
                                style={{ marginBottom: 8 }}
                                onClick={() => handleAddClick(HAUniversitiesEndorsementKey)}
                            >
                                Add
                            </CustomButton>
                            <CustomTable
                                disableOnPermission={disableOnPermission}
                                isEditAllow={true}
                                onEdit={(row) => handleEditClick(row, HAUniversitiesEndorsementKey)}
                                isDeleteAllow={true}
                                onDelete={(row) => deleteRow(row, HAUniversitiesEndorsementKey)}
                                columns={HAUniversitiesEndorsementColumns}
                                rows={mapShowRow?.(HAUniversitiesEndorsementRows)}
                                allowResize
                                fullWidthTable={false}
                                disableOldDataDelete={isAmendmentForm}
                                applicationCreateDtm={applicationCreateDtm}
                            />
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: 16 }}>
                        <Grid
                            container
                            style={{
                                fontStyle: 'italic',
                            }}
                        >
                            ⁵ If the study involved other departments, it is the Applicant's obligation to inform and
                            obtain agreement with the COS(s) or Head(s) of the Department(s) in HA or in Universities
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', margin: '8px 0 24px' }} />
                </Grid>
            </Paper>
        </div>
    );
}
